import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BigFiveDimension, bigFiveDimensions, getQuestionsByDimension } from '../data/bigFiveQuestions';
import { DimensionScore } from '../services/bigFiveAnalysis';
import DimensionTest from './DimensionTest';
import { statsStorage } from '../services/persistentStorage';

export default function DimensionSelector() {
  const [selectedDimension, setSelectedDimension] = useState<BigFiveDimension['key'] | null>(null);
  const [completedDimensions, setCompletedDimensions] = useState<Map<string, DimensionScore>>(new Map());
  const [hasRecordedCompletion, setHasRecordedCompletion] = useState(false);

  // 检查是否所有可用维度都已完成
  useEffect(() => {
    const availableDimensions = bigFiveDimensions.filter(d => getQuestionsByDimension(d.key).length > 0);
    const isAllCompleted = availableDimensions.length > 0 && 
                          availableDimensions.every(d => completedDimensions.has(d.key));
    
    if (isAllCompleted && !hasRecordedCompletion) {
      // 记录测评完成
      statsStorage.recordCompletion('big-five-personality')
        .then(() => {
          console.log('大五人格测评完成已记录');
          setHasRecordedCompletion(true);
        })
        .catch(error => {
          console.error('记录测评完成失败:', error);
        });
    }
  }, [completedDimensions, hasRecordedCompletion]);

  // 处理维度测试完成
  const handleDimensionComplete = (dimension: BigFiveDimension['key'], result: DimensionScore) => {
    const newCompleted = new Map(completedDimensions);
    newCompleted.set(dimension, result);
    setCompletedDimensions(newCompleted);
    setSelectedDimension(null);
  };

  // 返回选择页面
  const handleBack = () => {
    setSelectedDimension(null);
  };

  // 如果选择了维度，显示测试页面
  if (selectedDimension) {
    return (
      <DimensionTest
        dimension={selectedDimension}
        onComplete={(result) => handleDimensionComplete(selectedDimension, result)}
        onBack={handleBack}
      />
    );
  }

  // 显示维度选择页面
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] to-[#E9ECEF] py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* 头部 */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              大五人格测评
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              选择一个维度开始测评，每个维度包含10道情境题
            </p>
          </motion.div>
        </div>

        {/* 维度卡片网格 */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {bigFiveDimensions.map((dimension, index) => {
            const questionsCount = getQuestionsByDimension(dimension.key).length;
            const isCompleted = completedDimensions.has(dimension.key);
            const result = completedDimensions.get(dimension.key);
            const isAvailable = questionsCount > 0;

            return (
              <motion.div
                key={dimension.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 ${
                  isAvailable ? 'hover:shadow-xl hover:scale-105 cursor-pointer' : 'opacity-60'
                }`}
                onClick={() => isAvailable && setSelectedDimension(dimension.key)}
              >
                <div className="p-8 text-center relative">
                  {/* 状态指示器 */}
                  <div className="absolute top-4 right-4">
                    {isCompleted && (
                      <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                        已完成
                      </div>
                    )}
                    {!isAvailable && (
                      <div className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-medium">
                        开发中
                      </div>
                    )}
                  </div>

                  {/* 图标和名称居中 */}
                  <div className="mb-6">
                    <div className="text-6xl mb-4">{dimension.icon}</div>
                    <h3 className="text-2xl font-bold text-gray-800">
                      {dimension.name}
                    </h3>
                  </div>

                  {/* 完成状态显示 */}
                  {isCompleted && result && (
                    <div className="bg-gradient-to-r from-[#3A7D78]/10 to-[#F5B6C1]/10 rounded-lg p-3 mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">得分</span>
                        <span className="text-lg font-bold text-[#3A7D78]">{result.percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-[#3A7D78] to-[#F5B6C1] h-2 rounded-full"
                          style={{ width: `${result.percentage}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {result.level === 'very_high' && '非常高'}
                        {result.level === 'high' && '较高'}
                        {result.level === 'moderate' && '中等'}
                        {result.level === 'low' && '较低'}
                        {result.level === 'very_low' && '很低'}
                      </div>
                    </div>
                  )}

                  {/* 行动按钮 */}
                  <div className="flex justify-center">
                    {isAvailable ? (
                      <button className="w-full bg-[#3A7D78] text-white py-3 px-6 rounded-lg hover:bg-[#2F6B67] transition-colors font-medium">
                        {isCompleted ? '重新测试' : '开始测试'}
                      </button>
                    ) : (
                      <button className="w-full bg-gray-200 text-gray-500 py-3 px-6 rounded-lg cursor-not-allowed font-medium">
                        敬请期待
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* 统计信息 */}
        {completedDimensions.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6"
          >
            <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
              测评进度
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {bigFiveDimensions.map((dimension) => {
                const isCompleted = completedDimensions.has(dimension.key);
                const result = completedDimensions.get(dimension.key);
                const questionsCount = getQuestionsByDimension(dimension.key).length;
                const isAvailable = questionsCount > 0;

                return (
                  <div key={dimension.key} className="text-center">
                    <div className={`text-2xl mb-2 ${
                      isCompleted ? 'opacity-100' : isAvailable ? 'opacity-50' : 'opacity-30'
                    }`}>
                      {dimension.icon}
                    </div>
                    <div className="text-sm font-medium text-gray-700 mb-1">
                      {dimension.name}
                    </div>
                    {isCompleted && result ? (
                      <div className="text-lg font-bold text-[#3A7D78]">
                        {result.percentage}%
                      </div>
                    ) : isAvailable ? (
                      <div className="text-sm text-gray-500">未完成</div>
                    ) : (
                      <div className="text-sm text-gray-400">开发中</div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="mt-6 text-center">
              <div className="text-sm text-gray-600">
                已完成 {completedDimensions.size} / {bigFiveDimensions.filter(d => getQuestionsByDimension(d.key).length > 0).length} 个可用维度
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}